import type { Resolution as FirebaseResolution } from '../firebase/schema';
import type { DeviceType } from '../constants/device';
import type { ImageInfo, ValidationWarning, DeviceResolution } from '../types/device';
import { deviceRecommendationService } from './deviceRecommendationService';

export interface CloudinaryUrlInfo {
  isValid: boolean;
  cloudName?: string;
  publicId?: string;
  version?: string;
  format?: string;
  baseUrl?: string;
  resourceType?: 'image' | 'video';
}

export interface ProcessedWallpaperUrls {
  originalUrl: string;
  thumbnailUrl: string;
  resolutions: FirebaseResolution[];
}

export interface ProcessedWallpaperMediaUrls extends ProcessedWallpaperUrls {
  mediaType: 'image' | 'video';
  isAnimated: boolean;
}

export interface ProcessedDeviceUrls {
  originalUrl: string;
  thumbnailUrl: string;
  optimizedUrl: string;
  resolutions: FirebaseResolution[];
  deviceSpecificUrls: {
    [key: string]: string; // Different transformations for the device
  };
}

export interface ValidationResult {
  isValid: boolean;
  imageInfo: ImageInfo;
  warnings: ValidationWarning[];
  qualityScore: number;
}

class CloudinaryService {
  // Standard resolution configurations
  private readonly RESOLUTIONS = [
    { width: 720, height: 1280, size: 'mobile', label: 'Mobile (720x1280)' },
    { width: 1080, height: 1920, size: 'mobile-hd', label: 'Mobile HD (1080x1920)' },
    { width: 1024, height: 768, size: 'tablet', label: 'Tablet (1024x768)' },
    { width: 1366, height: 768, size: 'laptop', label: 'Laptop (1366x768)' },
    { width: 1920, height: 1080, size: 'desktop', label: 'Desktop (1920x1080)' },
    { width: 2560, height: 1440, size: 'desktop-hd', label: 'Desktop HD (2560x1440)' },
    { width: 3840, height: 2160, size: '4k', label: '4K (3840x2160)' }
  ];

  /**
   * Validates if a URL is a valid Cloudinary media URL
   */
  validateCloudinaryUrl(url: string): CloudinaryUrlInfo {
    try {
      // Handle placeholder URLs (used for creating empty variations)
      if (url.startsWith('placeholder://') || url === '') {
        return { isValid: false };
      }

      const urlObj = new URL(url);
      
      // Check if it's a Cloudinary URL
      if (!urlObj.hostname.includes('cloudinary.com')) {
        return { isValid: false };
      }

      // Parse Cloudinary URL structure
      // Format: https://res.cloudinary.com/{cloud_name}/{resource_type}/{type}/{version}/{public_id}.{format}
      const pathParts = urlObj.pathname.split('/').filter(part => part.length > 0);
      
      if (pathParts.length < 4) {
        return { isValid: false };
      }

      const cloudName = pathParts[0];
      const resourceType = pathParts[1]; // image or video
      // const type = pathParts[2]; // usually 'upload' - not used currently
      
      if (resourceType !== 'image' && resourceType !== 'video') {
        return { isValid: false };
      }

      // Extract public ID and format
      const lastPart = pathParts[pathParts.length - 1];
      const dotIndex = lastPart.lastIndexOf('.');
      const format = dotIndex > -1 ? lastPart.substring(dotIndex + 1) : '';
      
      // Check if there's a version (starts with 'v' followed by numbers)
      let publicId = '';
      let version = '';
      
      if (pathParts.length >= 5 && pathParts[3].match(/^v\d+$/)) {
        version = pathParts[3];
        publicId = pathParts.slice(4).join('/');
        if (dotIndex > -1) {
          publicId = publicId.substring(0, publicId.lastIndexOf('.'));
        }
      } else {
        publicId = pathParts.slice(3).join('/');
        if (dotIndex > -1) {
          publicId = publicId.substring(0, publicId.lastIndexOf('.'));
        }
      }

      const baseUrl = `https://res.cloudinary.com/${cloudName}/${resourceType}/upload`;

      return {
        isValid: true,
        cloudName,
        publicId,
        version,
        format: format || 'jpg',
        baseUrl,
        resourceType
      };
    } catch (error) {
      console.error('Error parsing Cloudinary URL:', error);
      return { isValid: false };
    }
  }

  /**
   * Checks if an image URL is accessible
   */
  async checkImageAccessibility(url: string): Promise<boolean> {
    try {
      const response = await fetch(url, { 
        method: 'HEAD',
        mode: 'cors'
      });
      return response.ok;
    } catch (error) {
      console.error('Error checking image accessibility:', error);
      return false;
    }
  }

  /**
   * Generates a thumbnail URL from a Cloudinary URL
   */
  generateThumbnailUrl(originalUrl: string, width = 400, height = 300): string {
    const urlInfo = this.validateCloudinaryUrl(originalUrl);
    
    if (!urlInfo.isValid || !urlInfo.baseUrl || !urlInfo.publicId) {
      throw new Error('Invalid Cloudinary URL');
    }

    const versionPart = urlInfo.version ? `/${urlInfo.version}` : '';
    const thumbnailTransforms = urlInfo.resourceType === 'video'
      ? `so_0,c_fit,w_${width},h_${height},q_auto,f_jpg`
      : `c_fit,w_${width},h_${height},q_auto,f_auto`;
    
    return `${urlInfo.baseUrl}/${thumbnailTransforms}${versionPart}/${urlInfo.publicId}.${urlInfo.format}`;
  }

  /**
   * Generates multiple resolution URLs from a Cloudinary URL
   */
  generateResolutionUrls(originalUrl: string): FirebaseResolution[] {
    const urlInfo = this.validateCloudinaryUrl(originalUrl);
    
    if (!urlInfo.isValid || !urlInfo.baseUrl || !urlInfo.publicId) {
      throw new Error('Invalid Cloudinary URL');
    }

    const versionPart = urlInfo.version ? `/${urlInfo.version}` : '';
    
    return this.RESOLUTIONS.map(resolution => {
      const transformations = `c_fit,w_${resolution.width},h_${resolution.height},q_auto,f_auto`;
      const url = `${urlInfo.baseUrl}/${transformations}${versionPart}/${urlInfo.publicId}.${urlInfo.format}`;
      
      return {
        width: resolution.width,
        height: resolution.height,
        url,
        size: resolution.size
      };
    });
  }

  /**
   * Processes a Cloudinary URL and returns all necessary URLs
   */
  async processWallpaperUrl(originalUrl: string): Promise<ProcessedWallpaperUrls> {
    // Validate URL format
    const urlInfo = this.validateCloudinaryUrl(originalUrl);
    if (!urlInfo.isValid) {
      throw new Error('Invalid Cloudinary URL format');
    }

    // Check if media is accessible
    const isAccessible = await this.checkImageAccessibility(originalUrl);
    if (!isAccessible) {
      throw new Error('Media is not accessible from the provided URL');
    }

    // Generate thumbnail and resolution URLs
    const thumbnailUrl = this.generateThumbnailUrl(originalUrl);
    const resolutions = this.generateResolutionUrls(originalUrl);

    return {
      originalUrl,
      thumbnailUrl,
      resolutions
    };
  }

  /**
   * Extracts image dimensions from Cloudinary URL (if available in transformations)
   */
  extractDimensionsFromUrl(url: string): { width?: number; height?: number } {
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');
      
      // Look for transformation parameters
      for (const part of pathParts) {
        if (part.includes('w_') || part.includes('h_')) {
          const transformations = part.split(',');
          let width: number | undefined;
          let height: number | undefined;
          
          for (const transform of transformations) {
            if (transform.startsWith('w_')) {
              width = parseInt(transform.substring(2));
            } else if (transform.startsWith('h_')) {
              height = parseInt(transform.substring(2));
            }
          }
          
          return { width, height };
        }
      }
      
      return {};
    } catch (error) {
      console.error('Error extracting dimensions:', error);
      return {};
    }
  }

  /**
   * Optimizes a Cloudinary URL for better performance
   */
  optimizeUrl(url: string, options: {
    quality?: 'auto' | number;
    format?: 'auto' | 'webp' | 'jpg' | 'png';
    width?: number;
    height?: number;
  } = {}): string {
    const urlInfo = this.validateCloudinaryUrl(url);
    
    if (!urlInfo.isValid || !urlInfo.baseUrl || !urlInfo.publicId) {
      return url; // Return original if can't optimize
    }

    const {
      quality = 'auto',
      format = 'auto',
      width,
      height
    } = options;

  const transformations: string[] = [];
    
    if (width && height) {
      transformations.push(`c_fit,w_${width},h_${height}`);
    } else if (width) {
      transformations.push(`w_${width}`);
    } else if (height) {
      transformations.push(`h_${height}`);
    }
    
    // Network-aware quality
    let qualityToken = `q_${quality}`;
    try {
      const connection = (navigator as any)?.connection || (navigator as any)?.mozConnection || (navigator as any)?.webkitConnection;
      const saveData = !!connection?.saveData;
      const effectiveType = String(connection?.effectiveType || '').toLowerCase();
      if (saveData) qualityToken = 'q_auto:eco';
      else if (/^2g$|^slow-2g$/.test(effectiveType)) qualityToken = 'q_auto:eco';
      else if (/3g/.test(effectiveType)) qualityToken = 'q_auto:low';
    } catch (_err) {
      // Fallback: leave default quality token
    }
    transformations.push(qualityToken);
    transformations.push(`f_${format}`);
    transformations.push('dpr_auto');
    transformations.push('fl_progressive');
    
    const versionPart = urlInfo.version ? `/${urlInfo.version}` : '';
    const transformationString = transformations.join(',');
    
    return `${urlInfo.baseUrl}/${transformationString}${versionPart}/${urlInfo.publicId}.${urlInfo.format}`;
  }

  /**
   * Gets available resolution options
   */
  getAvailableResolutions(): Array<{ width: number; height: number; size: string; label: string }> {
    return [...this.RESOLUTIONS];
  }

  /**
   * Process device variation with device-specific URL processing
   */
  async processDeviceVariation(originalUrl: string, device: DeviceType): Promise<ProcessedDeviceUrls> {
    // Validate URL format
    const urlInfo = this.validateCloudinaryUrl(originalUrl);
    if (!urlInfo.isValid) {
      throw new Error('Invalid Cloudinary URL format');
    }

    // Check if image is accessible
    const isAccessible = await this.checkImageAccessibility(originalUrl);
    if (!isAccessible) {
      throw new Error('Image is not accessible from the provided URL');
    }

    // Get device specifications for targeted processing
    const deviceSpec = deviceRecommendationService.getDeviceSpecification(device);
    if (!deviceSpec) {
      throw new Error(`Unknown device type: ${device}`);
    }

    // Generate device-optimized thumbnail
    const thumbnailUrl = this.generateDeviceThumbnail(originalUrl, device);

    // Generate device-optimized main URL
    const optimizedUrl = this.generateDeviceOptimizedUrl(originalUrl, device);

    // Generate device-aware resolutions
    const resolutions = this.generateDeviceResolutions(originalUrl, device);

    // Generate device-specific URLs for different use cases
    const deviceSpecificUrls = this.generateDeviceSpecificUrls(originalUrl, device);

    return {
      originalUrl,
      thumbnailUrl,
      optimizedUrl,
      resolutions,
      deviceSpecificUrls
    };
  }

  /**
   * Generate device-optimized URL with device-targeted transformations
   */
  generateDeviceOptimizedUrl(originalUrl: string, device: DeviceType, resolution?: { width: number; height: number }): string {
    const urlInfo = this.validateCloudinaryUrl(originalUrl);
    
    if (!urlInfo.isValid || !urlInfo.baseUrl || !urlInfo.publicId) {
      throw new Error('Invalid Cloudinary URL');
    }

    const deviceSpec = deviceRecommendationService.getDeviceSpecification(device);
    if (!deviceSpec) {
      throw new Error(`Unknown device type: ${device}`);
    }

    const versionPart = urlInfo.version ? `/${urlInfo.version}` : '';
    const transformations: string[] = [];

    // Use provided resolution or get optimal resolution for device
    let targetResolution = resolution;
    if (!targetResolution) {
      const optimalResolution = deviceSpec.recommendedResolutions.find((r: DeviceResolution) => r.isOptimal);
      if (optimalResolution) {
        targetResolution = { width: optimalResolution.width, height: optimalResolution.height };
      }
    }

    if (targetResolution) {
      // Use different crop strategies based on device type
      let cropStrategy = 'c_fit'; // Default
      
      switch (device) {
        case 'mobile_phone':
          cropStrategy = 'c_fill,g_center'; // Fill and center for mobile
          break;
        case 'tablet':
          cropStrategy = 'c_fit'; // Fit for tablets to avoid cropping
          break;
        case 'desktop':
        case 'ultrawide':
          cropStrategy = 'c_fill,g_center'; // Fill and center for desktop/ultrawide
          break;
      }

      transformations.push(`${cropStrategy},w_${targetResolution.width},h_${targetResolution.height}`);
    }

    // Device-specific quality with network-aware override
    const deviceQuality = this.getDeviceOptimalQuality(device);
    let qualityToken = typeof deviceQuality === 'string' ? `q_${deviceQuality}` : `q_${deviceQuality}`;
    try {
      const connection = (navigator as any)?.connection || (navigator as any)?.mozConnection || (navigator as any)?.webkitConnection;
      const saveData = !!connection?.saveData;
      const effectiveType = String(connection?.effectiveType || '').toLowerCase();
      if (saveData) qualityToken = 'q_auto:eco';
      else if (/^2g$|^slow-2g$/.test(effectiveType)) qualityToken = 'q_auto:eco';
      else if (/3g/.test(effectiveType)) qualityToken = 'q_auto:low';
    } catch (_err) {
      // Fallback: leave device quality selection
    }
    transformations.push(qualityToken);

    // Device-specific format optimization
    const format = urlInfo.resourceType === 'video'
      ? this.getDeviceOptimalVideoFormat(device)
      : this.getDeviceOptimalFormat(device);
    transformations.push(`f_${format}`);
    transformations.push('dpr_auto');
    transformations.push('fl_progressive');

    // Add device-specific enhancements
    const enhancements = this.getDeviceEnhancements(device);
    if (enhancements.length > 0) {
      transformations.push(...enhancements);
    }

    const transformationString = transformations.join(',');
    return `${urlInfo.baseUrl}/${transformationString}${versionPart}/${urlInfo.publicId}.${urlInfo.format}`;
  }

  /**
   * Validate media for device with device-specific validation
   */
  async validateMediaForDevice(mediaUrl: string, device: DeviceType): Promise<ValidationResult> {
    try {
      // First validate the URL format
      const urlInfo = this.validateCloudinaryUrl(mediaUrl);
      if (!urlInfo.isValid) {
        throw new Error('Invalid Cloudinary URL format');
      }

      // Check accessibility
      const isAccessible = await this.checkImageAccessibility(mediaUrl);
      if (!isAccessible) {
        throw new Error('Media is not accessible');
      }

      // Extract or fetch media information
      const mediaInfo = await this.extractImageInfo(mediaUrl);

      // Use device recommendation service for validation
      const warnings = deviceRecommendationService.validateImageForDevice(mediaInfo, device);
      const qualityScore = deviceRecommendationService.calculateQualityScore(mediaInfo, device);

      // Update image info with quality score
      mediaInfo.qualityScore = qualityScore;

      return {
        isValid: warnings.every(w => w.severity !== 'error'),
        imageInfo: mediaInfo,
        warnings,
        qualityScore
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Validation failed';
      
      return {
        isValid: false,
        imageInfo: {
          width: 0,
          height: 0,
          aspectRatio: '0:0',
          format: 'unknown',
          fileSize: 'unknown'
        },
        warnings: [{
          type: 'format',
          message: errorMessage,
          severity: 'error'
        }],
        qualityScore: 0
      };
    }
  }

  /**
   * Backwards-compatible alias for callers that still use image validation naming.
   */
  async validateImageForDevice(imageUrl: string, device: DeviceType): Promise<ValidationResult> {
    return this.validateMediaForDevice(imageUrl, device);
  }

  /**
   * Generate device-aware resolution URLs
   */
  private generateDeviceResolutions(originalUrl: string, device: DeviceType): FirebaseResolution[] {
    const deviceSpec = deviceRecommendationService.getDeviceSpecification(device);
    if (!deviceSpec) {
      return this.generateResolutionUrls(originalUrl); // Fallback to standard resolutions
    }

    const urlInfo = this.validateCloudinaryUrl(originalUrl);
    if (!urlInfo.isValid || !urlInfo.baseUrl || !urlInfo.publicId) {
      throw new Error('Invalid Cloudinary URL');
    }

    const versionPart = urlInfo.version ? `/${urlInfo.version}` : '';

    // Use device-specific recommended resolutions
    return deviceSpec.recommendedResolutions.map((resolution: DeviceResolution) => {
      const transformations = `c_fit,w_${resolution.width},h_${resolution.height},q_auto,f_auto`;
      const url = `${urlInfo.baseUrl}/${transformations}${versionPart}/${urlInfo.publicId}.${urlInfo.format}`;
      
      return {
        width: resolution.width,
        height: resolution.height,
        url,
        size: resolution.isOptimal ? `${device}-optimal` : `${device}-standard`
      };
    });
  }

  /**
   * Generate device-specific thumbnail
   */
  private generateDeviceThumbnail(originalUrl: string, device: DeviceType): string {
    const deviceSpec = deviceRecommendationService.getDeviceSpecification(device);
    
    // Use device-appropriate thumbnail dimensions
    let thumbWidth = 400;
    let thumbHeight = 300;
    
    if (deviceSpec) {
      const optimalAspectRatio = deviceRecommendationService.getOptimalAspectRatio(device);
      if (optimalAspectRatio) {
        // Calculate thumbnail dimensions based on device aspect ratio
        const ratio = optimalAspectRatio.width / optimalAspectRatio.height;
        if (ratio > 1) {
          // Landscape
          thumbWidth = 400;
          thumbHeight = Math.round(400 / ratio);
        } else {
          // Portrait
          thumbHeight = 400;
          thumbWidth = Math.round(400 * ratio);
        }
      }
    }

    return this.generateThumbnailUrl(originalUrl, thumbWidth, thumbHeight);
  }

  /**
   * Generate device-specific URLs for different use cases
   */
  private generateDeviceSpecificUrls(originalUrl: string, device: DeviceType): { [key: string]: string } {
    const urls: { [key: string]: string } = {};

    try {
      // Preview URL (smaller, optimized for previews)
      urls.preview = this.generateDeviceOptimizedUrl(originalUrl, device, { width: 800, height: 600 });

      // High quality URL (for downloads)
      urls.highQuality = this.optimizeUrl(originalUrl, { quality: 90, format: 'auto' });

      // Compressed URL (for faster loading)
      urls.compressed = this.optimizeUrl(originalUrl, { quality: 70, format: 'webp' });

      // Device-specific mockup URL (if needed for previews)
      urls.mockup = this.generateDeviceOptimizedUrl(originalUrl, device);

    } catch (error) {
      console.error('Error generating device-specific URLs:', error);
    }

    return urls;
  }

  /**
   * Get optimal quality setting for device type
   */
  private getDeviceOptimalQuality(device: DeviceType): string | number {
    switch (device) {
      case 'mobile_phone':
        return 80; // Slightly compressed for mobile data usage
      case 'tablet':
        return 85; // Good balance for tablet displays
      case 'desktop':
        return 90; // High quality for desktop displays
      case 'ultrawide':
        return 95; // Highest quality for large displays
      default:
        return 'auto';
    }
  }

  /**
   * Get optimal format for video assets by device type
   */
  private getDeviceOptimalVideoFormat(device: DeviceType): string {
    switch (device) {
      case 'mobile_phone':
      case 'tablet':
        return 'mp4';
      case 'desktop':
      case 'ultrawide':
        return 'mp4';
      default:
        return 'mp4';
    }
  }

  /**
   * Get optimal format for device type
   */
  private getDeviceOptimalFormat(device: DeviceType): string {
    switch (device) {
      case 'mobile_phone':
        return 'webp'; // Better compression for mobile
      case 'tablet':
        return 'webp'; // Good compression and quality
      case 'desktop':
      case 'ultrawide':
        return 'auto'; // Let Cloudinary decide based on browser support
      default:
        return 'auto';
    }
  }

  /**
   * Get device-specific enhancement transformations
   */
  private getDeviceEnhancements(device: DeviceType): string[] {
    const enhancements: string[] = [];

    switch (device) {
      case 'mobile_phone':
        enhancements.push('e_sharpen:100'); // Slight sharpening for small screens
        break;
      case 'tablet':
        enhancements.push('e_improve'); // General improvement
        break;
      case 'desktop':
      case 'ultrawide':
        enhancements.push('e_improve:outdoor'); // Enhanced for larger displays
        break;
    }

    return enhancements;
  }

  /**
   * Get image information from URL (public method)
   */
  async getImageInfo(imageUrl: string): Promise<ImageInfo> {
    return this.extractImageInfo(imageUrl);
  }

  /**
   * Extract comprehensive image information from URL
   */
  private async extractImageInfo(imageUrl: string): Promise<ImageInfo> {
    // In a real implementation, you would use Cloudinary's API to get actual image metadata
    // For now, we'll extract what we can from the URL and provide reasonable defaults
    
    const dimensions = this.extractDimensionsFromUrl(imageUrl);
    const urlInfo = this.validateCloudinaryUrl(imageUrl);
    
    // Calculate aspect ratio
    let aspectRatio = '16:9'; // Default
    if (dimensions.width && dimensions.height) {
      const gcd = this.calculateGCD(dimensions.width, dimensions.height);
      const ratioWidth = dimensions.width / gcd;
      const ratioHeight = dimensions.height / gcd;
      aspectRatio = `${ratioWidth}:${ratioHeight}`;
    }

    return {
      width: dimensions.width || 1920, // Default values
      height: dimensions.height || 1080,
      aspectRatio,
      format: urlInfo.format || 'jpg',
      fileSize: 'Unknown', // Would need API call to get actual size
      qualityScore: undefined // Will be calculated by validation
    };
  }

  /**
   * Calculate Greatest Common Divisor for aspect ratio calculation
   */
  private calculateGCD(a: number, b: number): number {
    return b === 0 ? a : this.calculateGCD(b, a % b);
  }
}

export const cloudinaryService = new CloudinaryService();